import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useCustomerAuth } from '@/hooks/useCustomerAuth';

interface Review {
  id: string;
  product_name: string;
  customer_phone: string;
  customer_name: string;
  rating: number;
  review_text?: string;
  images: string[];
  helpful_count: number;
  created_at: string;
}

interface ProductReviewsProps {
  productName: string;
  productCategory?: string;
}

function StarRating({
  value,
  onChange,
  size = 'md',
  readonly = false,
}: {
  value: number;
  onChange?: (v: number) => void;
  size?: 'sm' | 'md' | 'lg';
  readonly?: boolean;
}) {
  const [hover, setHover] = useState(0);
  const sizes = { sm: 'text-sm', md: 'text-xl', lg: 'text-3xl' };
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={readonly}
          onClick={() => onChange?.(star)}
          onMouseEnter={() => !readonly && setHover(star)}
          onMouseLeave={() => setHover(0)}
          className={`transition-transform ${readonly ? '' : 'cursor-pointer hover:scale-110'} ${sizes[size]}`}
        >
          <i
            className={
              (hover || value) >= star
                ? 'ri-star-fill text-amber-400'
                : 'ri-star-line text-gray-300'
            }
          ></i>
        </button>
      ))}
    </div>
  );
}

function RatingSummary({ reviews }: { reviews: Review[] }) {
  if (reviews.length === 0) return null;
  const avg = reviews.reduce((s, r) => s + r.rating, 0) / reviews.length;
  const counts = [5, 4, 3, 2, 1].map((star) => ({
    star,
    count: reviews.filter((r) => r.rating === star).length,
  }));
  return (
    <div className="flex items-center gap-4 bg-gray-50 rounded-2xl p-4 mb-4">
      <div className="text-center shrink-0">
        <div className="text-4xl font-extrabold text-gray-900 leading-none">{avg.toFixed(1)}</div>
        <StarRating value={Math.round(avg)} size="sm" readonly />
        <p className="text-gray-400 text-[10px] mt-0.5">{reviews.length} reviews</p>
      </div>
      <div className="flex-1 space-y-1">
        {counts.map(({ star, count }) => {
          const pct = reviews.length > 0 ? (count / reviews.length) * 100 : 0;
          return (
            <div key={star} className="flex items-center gap-2">
              <span className="text-[11px] text-gray-500 w-3">{star}</span>
              <i className="ri-star-fill text-amber-400 text-[10px] shrink-0"></i>
              <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-amber-400 rounded-full transition-all duration-500"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="text-[10px] text-gray-400 w-4 text-right">{count}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function ProductReviews({ productName, productCategory }: ProductReviewsProps) {
  const { user, isLoggedIn } = useCustomerAuth();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  // Form state
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState('');
  const [helpfulVoted, setHelpfulVoted] = useState<Set<string>>(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem('review_helpful_votes') || '[]'));
    } catch {
      return new Set();
    }
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('product_reviews')
      .select('*')
      .eq('product_name', productName)
      .eq('is_approved', true)
      .order('created_at', { ascending: false })
      .limit(50);
    if (!error && data) setReviews(data as Review[]);
    setLoading(false);
  }, [productName]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    if (uploadedImages.length + files.length > 4) {
      setSubmitMsg('Maximum 4 images allowed');
      return;
    }

    setUploading(true);
    const newUrls: string[] = [];

    for (const file of Array.from(files)) {
      if (file.size > 5 * 1024 * 1024) {
        setSubmitMsg('Each image must be under 5MB');
        continue;
      }
      const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const fileName = `review_${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;

      const { data, error } = await supabase.storage
        .from('review-images')
        .upload(fileName, file, { contentType: file.type, upsert: false });

      if (!error && data) {
        const { data: urlData } = supabase.storage
          .from('review-images')
          .getPublicUrl(data.path);
        if (urlData?.publicUrl) newUrls.push(urlData.publicUrl);
      }
    }

    setUploadedImages((prev) => [...prev, ...newUrls]);
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (url: string) => {
    setUploadedImages((prev) => prev.filter((u) => u !== url));
  };

  const handleSubmitReview = async (e: React.FormEvent) => {
    e.preventDefault();
    if (rating === 0) { setSubmitMsg('Please select a star rating'); return; }
    if (!isLoggedIn || !user) { setSubmitMsg('Please login to submit a review'); return; }

    setSubmitting(true);
    setSubmitMsg('');

    const { error } = await supabase.from('product_reviews').insert([{
      product_name: productName,
      product_category: productCategory ?? '',
      customer_phone: user.phone,
      customer_name: user.name || 'Customer',
      rating,
      review_text: reviewText.trim() || null,
      images: uploadedImages,
      is_approved: true,
    }]);

    if (error) {
      setSubmitMsg('Review submit karne mein problem aayi. Dobara try karo.');
    } else {
      setSubmitMsg('');
      setShowForm(false);
      setRating(0);
      setReviewText('');
      setUploadedImages([]);
      await fetchReviews();
    }
    setSubmitting(false);
  };

  const handleHelpful = async (reviewId: string, currentCount: number) => {
    if (helpfulVoted.has(reviewId)) return;
    const newCount = currentCount + 1;
    const { error } = await supabase
      .from('product_reviews')
      .update({ helpful_count: newCount })
      .eq('id', reviewId);
    if (!error) {
      setReviews((prev) =>
        prev.map((r) => (r.id === reviewId ? { ...r, helpful_count: newCount } : r))
      );
      const newVotes = new Set([...helpfulVoted, reviewId]);
      setHelpfulVoted(newVotes);
      localStorage.setItem('review_helpful_votes', JSON.stringify([...newVotes]));
    }
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const avgRating = reviews.length > 0
    ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length
    : 0;

  return (
    <div className="mt-5 pt-4 border-t border-gray-100">
      {/* Header Row */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-1 h-5 rounded-full bg-amber-400"></div>
          <h4 className="text-gray-900 font-bold text-sm">Customer Reviews</h4>
          {reviews.length > 0 && (
            <span className="flex items-center gap-1 bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-bold px-2 py-0.5 rounded-full">
              <i className="ri-star-fill text-amber-400 text-[10px]"></i>
              {avgRating.toFixed(1)} ({reviews.length})
            </span>
          )}
        </div>
        {isLoggedIn && (
          <button
            onClick={() => setShowForm((p) => !p)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gray-900 text-white text-xs font-bold cursor-pointer hover:bg-gray-700 transition-all whitespace-nowrap"
          >
            <i className={showForm ? 'ri-close-line' : 'ri-edit-line'}></i>
            {showForm ? 'Cancel' : 'Write Review'}
          </button>
        )}
        {!isLoggedIn && (
          <span className="text-gray-400 text-[10px] italic">Login to review</span>
        )}
      </div>

      {/* Rating Summary */}
      {reviews.length > 0 && <RatingSummary reviews={reviews} />}

      {/* Write Review Form */}
      {showForm && (
        <form
          onSubmit={handleSubmitReview}
          className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-4 mb-4"
        >
          <h5 className="text-gray-900 font-bold text-sm mb-3 flex items-center gap-2">
            <i className="ri-star-line text-amber-500"></i>
            Your Review for {productName}
          </h5>

          {/* Star Rating */}
          <div className="mb-3">
            <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Rating <span className="text-red-500">*</span></label>
            <StarRating value={rating} onChange={setRating} size="lg" />
            {rating > 0 && (
              <p className="text-xs text-amber-600 font-semibold mt-1">
                {['', 'Bahut Bura', 'Bura', 'Theek Hai', 'Acha', 'Bahut Acha'][rating]}
              </p>
            )}
          </div>

          {/* Review Text */}
          <div className="mb-3">
            <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Review (Optional)</label>
            <textarea
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value.slice(0, 500))}
              rows={3}
              placeholder="Product kaisa laga? Quality, fit, delivery — sab batao..."
              className="w-full px-3 py-2.5 rounded-xl border border-amber-200 bg-white text-sm text-gray-700 outline-none focus:border-amber-400 resize-none"
            />
            <p className="text-[10px] text-gray-400 text-right mt-0.5">{reviewText.length}/500</p>
          </div>

          {/* Image Upload */}
          <div className="mb-4">
            <label className="text-xs font-semibold text-gray-600 mb-2 block">
              Photos Add Karo <span className="text-gray-400 font-normal">(max 4, upto 5MB each)</span>
            </label>

            {/* Preview uploaded images */}
            {uploadedImages.length > 0 && (
              <div className="flex gap-2 flex-wrap mb-2">
                {uploadedImages.map((url) => (
                  <div key={url} className="relative w-16 h-16 rounded-xl overflow-hidden border border-amber-200 bg-gray-100">
                    <img src={url} alt="review" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeImage(url)}
                      className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-red-500 text-white flex items-center justify-center text-[9px] cursor-pointer"
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}

            {uploadedImages.length < 4 && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-dashed border-amber-300 text-amber-700 text-xs font-semibold hover:bg-amber-50 transition-all cursor-pointer disabled:opacity-60 whitespace-nowrap"
              >
                {uploading ? (
                  <><i className="ri-loader-4-line animate-spin"></i>Uploading...</>
                ) : (
                  <><i className="ri-camera-line text-sm"></i>Photo Upload Karo</>
                )}
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={handleImageUpload}
            />
          </div>

          {submitMsg && (
            <p className="text-red-500 text-xs mb-3 flex items-center gap-1">
              <i className="ri-error-warning-line"></i>{submitMsg}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting || rating === 0}
            className="w-full py-3 rounded-xl bg-amber-500 text-white font-bold text-sm hover:bg-amber-600 transition-all disabled:opacity-50 cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
          >
            {submitting ? (
              <><i className="ri-loader-4-line animate-spin"></i>Submit Ho Raha Hai...</>
            ) : (
              <><i className="ri-send-plane-fill"></i>Review Submit Karo</>
            )}
          </button>
        </form>
      )}

      {/* Reviews List */}
      {loading ? (
        <div className="flex items-center justify-center py-6 gap-2 text-gray-400">
          <i className="ri-loader-4-line animate-spin"></i>
          <span className="text-xs">Reviews load ho rahi hain...</span>
        </div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-6">
          <div className="w-12 h-12 rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center mx-auto mb-3">
            <i className="ri-star-line text-amber-400 text-xl"></i>
          </div>
          <p className="text-gray-500 text-sm font-medium">Abhi koi review nahi hai</p>
          <p className="text-gray-400 text-xs mt-0.5">Pehle review likhne wale bano!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reviews.map((review) => (
            <div key={review.id} className="bg-white border border-gray-100 rounded-2xl p-3.5">
              {/* Review Header */}
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-sm">
                      {(review.customer_name || 'C')[0].toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="text-gray-900 font-bold text-xs leading-tight">
                      {review.customer_name || 'Customer'}
                    </p>
                    <p className="text-gray-400 text-[10px]">{formatDate(review.created_at)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 bg-amber-50 px-2 py-1 rounded-lg border border-amber-100">
                  <i className="ri-star-fill text-amber-400 text-xs"></i>
                  <span className="text-amber-700 font-bold text-xs">{review.rating}</span>
                </div>
              </div>

              {/* Stars */}
              <div className="mb-2">
                <StarRating value={review.rating} size="sm" readonly />
              </div>

              {/* Review text */}
              {review.review_text && (
                <p className="text-gray-700 text-xs leading-relaxed mb-3">{review.review_text}</p>
              )}

              {/* Review Images */}
              {review.images && review.images.length > 0 && (
                <div className="flex gap-2 mb-3 flex-wrap">
                  {review.images.map((url, idx) => (
                    <a
                      key={idx}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-16 h-16 rounded-xl overflow-hidden border border-gray-200 block shrink-0"
                    >
                      <img
                        src={url}
                        alt={`review image ${idx + 1}`}
                        className="w-full h-full object-cover hover:scale-105 transition-transform"
                        loading="lazy"
                      />
                    </a>
                  ))}
                </div>
              )}

              {/* Helpful button */}
              <div className="flex items-center gap-2 pt-2 border-t border-gray-50">
                <button
                  onClick={() => handleHelpful(review.id, review.helpful_count)}
                  disabled={helpfulVoted.has(review.id)}
                  className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg transition-all cursor-pointer whitespace-nowrap ${
                    helpfulVoted.has(review.id)
                      ? 'bg-green-50 text-green-600 border border-green-200'
                      : 'bg-gray-50 text-gray-500 border border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  <i className={helpfulVoted.has(review.id) ? 'ri-thumb-up-fill' : 'ri-thumb-up-line'}></i>
                  Helpful {review.helpful_count > 0 && `(${review.helpful_count})`}
                </button>
                {helpfulVoted.has(review.id) && (
                  <span className="text-green-600 text-[10px] font-semibold">Thanks for your feedback!</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
